import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Gavel, FolderKanban, Clock, FileCheck, DollarSign, Award, XCircle, BarChart3, Briefcase, Send, CheckCircle, TrendingUp } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = v => v ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v) : '$0.00';

const CRMDashboard = ({ onNavigate }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/crm/dashboard/stats`, { credentials: 'include' });
      setStats(await res.json());
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-900" /></div>;
  if (!stats) return <div className="text-center py-16 text-gray-500">Failed to load dashboard data.</div>;

  const nav = (tab) => onNavigate && onNavigate(tab);

  return (
    <div className="space-y-8">
      {/* ── BID DASHBOARD ── */}
      <div>
        <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2" data-testid="bid-dashboard-header">
          <Gavel size={16} /> Bid Dashboard
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          <MetricCard testId="metric-open-bids" icon={Gavel} label="Open Bids" value={stats.open_bids ?? 0} color="blue" onClick={() => nav('crm-bids')} />
          <MetricCard testId="metric-due-week" icon={Clock} label="Due This Week" value={stats.bids_due_this_week ?? 0} color="amber" onClick={() => nav('crm-bids')} />
          <MetricCard testId="metric-awaiting" icon={FileCheck} label="Awaiting Result" value={stats.submitted_awaiting ?? 0} color="indigo" onClick={() => nav('crm-bids')} />
          <MetricCard testId="metric-open-value" icon={DollarSign} label="Open Bid Value" value={fmt(stats.total_open_bid_value)} color="emerald" onClick={() => nav('crm-bids')} />
          <MetricCard testId="metric-awarded" icon={Award} label="Awarded" value={stats.awarded_bids ?? 0} color="green" onClick={() => nav('crm-bids')} />
          <MetricCard testId="metric-lost-bids" icon={XCircle} label="Lost" value={stats.lost_bids ?? 0} color="red" onClick={() => nav('crm-bids')} />
          <MetricCard testId="metric-win-rate" icon={BarChart3} label="Win Rate" value={`${stats.bid_win_rate ?? 0}%`} color="cyan" />
        </div>
      </div>

      {/* ── PROJECT SNAPSHOT ── */}
      <div>
        <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2" data-testid="project-snapshot-header">
          <FolderKanban size={16} /> Project Snapshot
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MetricCard testId="metric-projects-accepted" icon={CheckCircle} label="Projects Accepted" value={stats.projects_accepted_count ?? 0} color="emerald" onClick={() => nav('crm-projects')} />
          <MetricCard testId="metric-projects-accepted-value" icon={DollarSign} label="Accepted Value" value={fmt(stats.projects_accepted_total)} color="emerald" onClick={() => nav('crm-projects')} />
          <MetricCard testId="metric-projects-submitted" icon={Send} label="Projects Submitted" value={stats.submitted_only_count ?? 0} color="amber" onClick={() => nav('crm-projects')} />
          <MetricCard testId="metric-projects-submitted-value" icon={DollarSign} label="Submitted Value" value={fmt(stats.submitted_only_total)} color="amber" onClick={() => nav('crm-projects')} />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
          <MetricCard testId="metric-total-profit" icon={TrendingUp} label="Total Profit" value={fmt(stats.total_profit)} color={stats.total_profit >= 0 ? 'emerald' : 'red'} onClick={() => nav('crm-projects')} />
          <MetricCard testId="metric-total-expenses" icon={DollarSign} label="Total Expenses" value={fmt(stats.total_expenses)} color="red" onClick={() => nav('crm-projects')} />
          <MetricCard testId="metric-profit-margin" icon={BarChart3} label="Profit Margin" value={`${stats.profit_margin ?? 0}%`} color={stats.profit_margin >= 0 ? 'emerald' : 'red'} onClick={() => nav('crm-projects')} />
        </div>
      </div>

      {/* ── ACTIVE PROJECTS ── */}
      <div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <MetricCard testId="metric-active-projects" icon={Briefcase} label="Active Projects" value={stats.active_projects ?? 0} color="blue" onClick={() => nav('crm-projects')} />
          <MetricCard testId="metric-active-project-value" icon={DollarSign} label="Active Project Value" value={fmt(stats.total_active_project_value)} color="emerald" onClick={() => nav('crm-projects')} />
          <Card data-testid="recently-awarded-card">
            <CardContent className="pt-5">
              <p className="text-xs font-semibold text-gray-500 mb-3 flex items-center gap-1"><Award size={12} /> Recently Awarded</p>
              {(stats.recently_awarded || []).length === 0 ? (
                <p className="text-gray-400 text-xs py-4 text-center">No awarded deals yet</p>
              ) : (
                <div className="space-y-2">
                  {stats.recently_awarded.map((d, i) => (
                    <div key={d.id || i} className="flex items-center justify-between text-xs">
                      <span className="font-medium text-slate-900 truncate mr-2">{d.title}</span>
                      <span className="text-emerald-600 font-semibold whitespace-nowrap">{fmt(d.projected_revenue || d.value)}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

const MetricCard = ({ testId, icon: Icon, label, value, color, onClick }) => (
  <Card data-testid={testId} className={onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''} onClick={onClick}>
    <CardContent className="pt-4 pb-3 text-center">
      <Icon className={`mx-auto mb-1.5 text-${color}-600`} size={20} />
      <p className="text-lg font-bold text-slate-900">{value}</p>
      <p className="text-[10px] text-gray-500 mt-0.5">{label}</p>
    </CardContent>
  </Card>
);

export default CRMDashboard;
